import { type default as CartesianAxisView } from 'echarts/lib/component/axis/CartesianAxisView';
import { Text } from 'echarts/lib/util/graphic';
import { TextStyleProps, Rect, BoundingRect } from 'zrender';
import ZRText from 'zrender/lib/graphic/Text';
import type { AxisElementInfos, AxisElementType } from './types';
import { Dictionary, TextCommonOption } from 'echarts/lib/util/types';
import { util } from 'echarts';
import { CartesianAxisOption } from 'echarts/lib/coord/cartesian/AxisModel';

/**
 * 从 view 实例获取轴标签元素
 */
export function getAxisLabelEls(compView: CartesianAxisView) {
  const labelEls = [] as Text[];

  compView.group.traverse(childEl => {
    if ((childEl.anid || '').startsWith('label_')) {
      labelEls.push(childEl as Text);
    }
  });

  return labelEls;
}

/**
 * 从 view 实例获取轴标题元素
 */
export function getAxisNameEls(compView: CartesianAxisView) {
  const nameEls = [] as ZRText[];

  compView.group.traverse(childEl => {
    if (childEl.anid === 'name') {
      nameEls.push(childEl as ZRText);
    }
  });

  return nameEls;
}

export const axisElementInfo: AxisElementInfos = {
  axisLine: anid => anid === 'line',
  axisLabel: anid => anid.startsWith('label_'),
  axisTick: anid => anid.startsWith('ticks_'),
  nameTextStyle: anid => anid === 'name',
  splitLine: anid => anid.startsWith('line_'),
  splitArea: anid => anid.startsWith('area_'),
  minorSplitLine: anid => anid.startsWith('minor_line_'),
  minorTick: anid => anid.startsWith('minorticks_')
};

/**
 * 为了坐标轴里各个部分的zr元素对应的echarts类型。
 * @description 因为dvZ配置项需要根据元素所属的类型（轴标签、轴线等）设置图形元素的层级,
 * 但是zr元素属于视图层，没有echarts里axisLabel、axisLine的概念，
 * 所以需要这样一个根据zr元素id确定其类型的方法
 */
export class AxisElementTypeDispatcher {
  types: AxisElementType[];

  constructor() {
    this.types = Object.keys(axisElementInfo) as AxisElementType[];
  }

  getElementType(anid: string) {
    let elType: AxisElementType;
    this.types.forEach(type => {
      if (axisElementInfo[type](anid)) {
        elType = type;
      }
    });
    return elType;
  }
}

const FONT_ATTR = ['fontSize', 'fontWeight'];

// ECharts的text属性和ZRender的text属性的映射关系
const TEXT_ATTR_MAPPING = {
  position: 'textPosition',
  align: 'textAlign',
  verticalAlign: 'textVerticalAlign',
  offset: 'textOffset',
  distance: 'textDistance',
  lineHeight: 'textLineHeight',
  padding: 'textPadding'
};

interface TextStyleOption extends TextCommonOption {
  rich?: Dictionary<TextCommonOption>;
}

/**
 * 将ECharts的style转换为ZRender的style
 * 主要是针对text的属性做了转换
 * @param style
 * @returns
 */
export function toZRenderStyle(style: TextStyleOption): TextStyleProps {
  const textStyle = util.clone(style);
  const fontAttr = {};
  Object.keys(style).forEach(key => {
    if (TEXT_ATTR_MAPPING[key]) {
      textStyle[TEXT_ATTR_MAPPING[key]] = style[key];
      delete textStyle[key];
    }

    if (FONT_ATTR.includes(key)) {
      fontAttr[key] = style[key];
    }
  });

  return textStyle as TextStyleProps;
}

/**
 * 预绘制axis.name，计算其实际占据的高宽
 * @param axisOption
 * @param isXAxis
 * @returns
 */
export function preRenderAxisName(
  axisOption: CartesianAxisOption,
  padding: number[] = [0, 0, 0, 0]
): Rect {
  const { name, nameTextStyle } = axisOption;
  if (!name || !nameTextStyle) {
    return;
  }
  const textStyle = toZRenderStyle(nameTextStyle);
  textStyle.text = name;

  const element = new Text({
    style: textStyle
  });

  const boundingRect = util.clone(element.getBoundingRect());

  let { width, height } = element.getBoundingRect();

  // 注意：仅当padding为负值时，才会对流式布局生效；padding为正值时，为默认的效果(可能会重叠到grid内部)
  const topPadding = Math.abs(padding[0]);
  const rightPadding = Math.abs(padding[1]);
  const bottomPadding = Math.abs(padding[2]);
  const leftPadding = Math.abs(padding[3]);

  if (topPadding > 0) {
    height += topPadding;
  }
  if (rightPadding > 0) {
    width += rightPadding;
  }
  if (bottomPadding > 0) {
    height += bottomPadding;
  }
  if (leftPadding > 0) {
    width += leftPadding;
  }

  boundingRect.height = height;
  boundingRect.width = width;
  return boundingRect;
}

/**
 * 预绘制axis.name，计算其实际占据的高宽
 * @param axisOption
 * @param isXAxis
 * @returns
 */
export function getHeightOfAxisLabel(axisOption: CartesianAxisOption, text = '1'): BoundingRect {
  if (!axisOption.axisLabel.show) {
    return 0;
  }
  const textStyle = toZRenderStyle(axisOption.axisLabel);
  // 仅计算高度，因此文本设置为1
  textStyle.text = text;

  const element = new Text({
    style: textStyle
  });

  return element.getBoundingRect();
}
